import { useHasPermission } from "@dexteel/mesf-core";
import { ChevronRight, ExpandMore } from "@mui/icons-material";
import {
	Box,
	Button,
	ClickAwayListener,
	Collapse,
	List,
	ListItemButton,
	ListItemText,
	MenuItem,
	MenuList,
	Paper,
	Popper,
	Typography,
} from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import {
	Fragment,
	type KeyboardEvent,
	type MouseEvent,
	useEffect,
	useRef,
	useState,
} from "react";
import { Link, type LinkProps, useLocation } from "react-router-dom";

export interface NavbarLeafConfig {
	label: string;
	to: LinkProps["to"];
	permission?: string;
}

export interface NavbarItemConfig extends Partial<NavbarLeafConfig> {
	label: string;
	items?: NavbarLeafConfig[];
}

export interface NavbarMenuConfig {
	id: string;
	label: string;
	to?: LinkProps["to"];
	items: NavbarItemConfig[];
	permission?: string;
}

interface NavbarMenuBarProps {
	menus: NavbarMenuConfig[];
	isMobile?: boolean;
	closeDrawer?: () => void;
	hoverSwitchDelayMs?: number;
}

type NavEntry = {
	key: string;
	label: string;
	to?: LinkProps["to"];
	children?: NavEntry[];
};

type VisibleMenu = NavEntry & { id: string; items: NavEntry[] };

const withLeadingSlash = (pathname: string) =>
	pathname && !pathname.startsWith("/") ? `/${pathname}` : pathname;

const splitTo = (to: LinkProps["to"]) => {
	if (typeof to !== "string") {
		return {
			pathname: withLeadingSlash(to.pathname ?? ""),
			search: to.search ?? "",
		};
	}
	const [pathname, query] = to.split("?");
	return {
		pathname: withLeadingSlash(pathname),
		search: query ? `?${query}` : "",
	};
};

const matchScore = (
	to: LinkProps["to"] | undefined,
	location: { pathname: string; search: string },
) => {
	if (to === undefined) return -1;
	const { pathname, search } = splitTo(to);
	if (!pathname) return -1;

	const basePath = pathname.replace(/\/$/, "");
	const pathMatches =
		location.pathname === pathname ||
		location.pathname.startsWith(`${basePath}/`);
	if (!pathMatches) return -1;

	const current = new URLSearchParams(location.search);
	const required = [...new URLSearchParams(search)];
	if (!required.every(([name, value]) => current.get(name) === value)) {
		return -1;
	}

	return pathname.length * 10 + required.length;
};

const findActiveKey = (
	menus: VisibleMenu[],
	location: { pathname: string; search: string },
) => {
	let best: { key: string; score: number } | null = null;

	const visit = (entry: NavEntry) => {
		const score = matchScore(entry.to, location);
		if (score >= 0 && (best === null || score > best.score)) {
			best = { key: entry.key, score };
		}
		entry.children?.forEach(visit);
	};

	for (const menu of menus) {
		visit(menu);
		menu.items.forEach(visit);
	}

	return (best as { key: string } | null)?.key ?? null;
};

const MENU_POPPER_MODIFIERS = [{ name: "offset", options: { offset: [0, 6] } }];

const SUBMENU_POPPER_MODIFIERS = [
	{ name: "offset", options: { offset: [-4, 6] } },
];

const triggerSx = (active: boolean, open: boolean) => (theme: Theme) => ({
	position: "relative" as const,
	minWidth: 0,
	mr: 0.5,
	px: 1.25,
	py: 0.75,
	borderRadius: 1,
	color: theme.palette.common.white,
	fontSize: 14,
	fontWeight: active ? 600 : 500,
	textTransform: "none" as const,
	whiteSpace: "nowrap",
	backgroundColor: open
		? alpha(theme.palette.common.white, 0.16)
		: "transparent",
	"&:hover": { backgroundColor: alpha(theme.palette.common.white, 0.12) },
	"& .MuiButton-endIcon": { ml: 0.25, mr: -0.5 },
	"&::after": {
		content: '""',
		position: "absolute",
		left: 10,
		right: 10,
		bottom: 2,
		height: 2,
		borderRadius: 1,
		backgroundColor: theme.palette.common.white,
		opacity: active ? 1 : 0,
		transition: theme.transitions.create("opacity"),
	},
});

const chevronSx = (open: boolean) => (theme: Theme) => ({
	fontSize: 18,
	transform: open ? "rotate(180deg)" : "none",
	transition: theme.transitions.create("transform", {
		duration: theme.transitions.duration.shortest,
	}),
});

const menuItemSx = (theme: Theme) => ({
	mx: 0.5,
	my: 0.25,
	px: 1.5,
	py: 0.75,
	minHeight: 0,
	gap: 2,
	borderRadius: 1,
	fontSize: 14,
	color: theme.palette.text.primary,
	"&.Mui-selected": {
		backgroundColor: alpha(theme.palette.primary.main, 0.1),
		color: theme.palette.primary.main,
		"&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.16) },
	},
});

const mobileRowSx = (indent: number) => (theme: Theme) => ({
	"&&": {
		mx: 1,
		my: 0.25,
		py: 0.75,
		pl: indent,
		pr: 1.5,
		width: "auto",
		borderRadius: 1,
		color: theme.palette.text.primary,
		"& .MuiListItemText-primary": { fontSize: 14 },
	},
	"&&.Mui-selected": {
		backgroundColor: alpha(theme.palette.primary.main, 0.1),
		color: theme.palette.primary.main,
	},
});

export const NavbarMenuBar = ({
	menus,
	isMobile = false,
	closeDrawer,
	hoverSwitchDelayMs = 10,
}: NavbarMenuBarProps) => {
	const hasPermissionTo = useHasPermission();
	const location = useLocation();

	const triggerRefs = useRef<Record<string, HTMLElement | null>>({});
	const hoverTimeoutRef = useRef<number | null>(null);

	const [openMenuId, setOpenMenuId] = useState<string | null>(null);
	const [focusOnOpen, setFocusOnOpen] = useState(false);
	const [submenu, setSubmenu] = useState<{
		key: string;
		anchor: HTMLElement;
		focus: boolean;
	} | null>(null);

	const isAllowed = (permission?: string) =>
		!permission || hasPermissionTo(permission);

	const visibleMenus: VisibleMenu[] = menus
		.filter((menu) => isAllowed(menu.permission))
		.map((menu) => ({
			id: menu.id,
			key: menu.id,
			label: menu.label,
			to: menu.to,
			items: menu.items
				.filter((item) => isAllowed(item.permission))
				.map((item, index) => {
					const key = `${menu.id}${"::"}${index}`;
					return {
						key,
						label: item.label,
						to: item.to,
						children: item.items
							?.filter((leaf) => isAllowed(leaf.permission))
							.map((leaf, leafIndex) => ({
								key: `${key}${"::"}${leafIndex}`,
								label: leaf.label,
								to: leaf.to,
							})),
					};
				})
				.filter(
					(item) => item.to !== undefined || (item.children?.length ?? 0) > 0,
				),
		}))
		.filter((menu) => menu.items.length > 0 || menu.to !== undefined);

	const activeKey = findActiveKey(visibleMenus, location);

	const isActive = (key: string) =>
		activeKey !== null &&
		(activeKey === key || activeKey.startsWith(`${key}${"::"}`));

	const [expandedMenuIds, setExpandedMenuIds] = useState<string[]>(() =>
		visibleMenus.filter((menu) => isActive(menu.id)).map((menu) => menu.id),
	);

	const clearHoverTimeout = () => {
		if (hoverTimeoutRef.current !== null) {
			window.clearTimeout(hoverTimeoutRef.current);
			hoverTimeoutRef.current = null;
		}
	};

	const openMenu = (menuId: string, focus = false) => {
		clearHoverTimeout();
		setSubmenu(null);
		setFocusOnOpen(focus);
		setOpenMenuId(menuId);
	};

	const closeMenu = () => {
		clearHoverTimeout();
		setSubmenu(null);
		setOpenMenuId(null);
	};

	const navigate = () => {
		closeMenu();
		closeDrawer?.();
	};

	const openSubmenu = (key: string, anchor: HTMLElement, focus: boolean) => {
		if (submenu?.key === key && !focus) return;
		setSubmenu({ key, anchor, focus });
	};

	const closeSubmenu = () => setSubmenu(null);

	const handleTriggerHover = (menuId: string) => {
		if (openMenuId === null || openMenuId === menuId) return;
		clearHoverTimeout();
		hoverTimeoutRef.current = window.setTimeout(
			() => openMenu(menuId),
			hoverSwitchDelayMs,
		);
	};

	const handleTriggerKeyDown = (
		event: KeyboardEvent<HTMLElement>,
		menuId: string,
	) => {
		if (event.key === "ArrowDown") {
			event.preventDefault();
			openMenu(menuId, true);
		}
		if (event.key === "Escape" && openMenuId !== null) {
			event.preventDefault();
			closeMenu();
		}
	};

	const handleMenuKeyDown = (
		event: KeyboardEvent<HTMLElement>,
		menuId: string,
	) => {
		if (event.key === "Escape") {
			event.preventDefault();
			closeMenu();
			triggerRefs.current[menuId]?.focus();
		}
		if (event.key === "Tab") {
			closeMenu();
		}
	};

	const handleSubmenuKeyDown = (event: KeyboardEvent<HTMLElement>) => {
		if (event.key === "ArrowLeft" || event.key === "Escape") {
			event.preventDefault();
			event.stopPropagation();
			submenu?.anchor.focus();
			closeSubmenu();
		}
	};

	const toggleExpanded = (menuId: string) =>
		setExpandedMenuIds((current) =>
			current.includes(menuId)
				? current.filter((id) => id !== menuId)
				: [...current, menuId],
		);

	useEffect(() => () => clearHoverTimeout(), []);

	if (isMobile) {
		const renderMobileLink = (entry: NavEntry, indent: number) => (
			<ListItemButton
				key={entry.key}
				component={Link}
				to={entry.to ?? "#"}
				selected={isActive(entry.key)}
				onClick={navigate}
				sx={mobileRowSx(indent)}
			>
				<ListItemText primary={entry.label} />
			</ListItemButton>
		);

		return (
			<List disablePadding sx={{ width: "100%", py: 1 }}>
				{visibleMenus.map((menu) => {
					if (menu.items.length === 0) return renderMobileLink(menu, 1.5);

					const expanded = expandedMenuIds.includes(menu.id);

					return (
						<Fragment key={menu.id}>
							<ListItemButton
								onClick={() => toggleExpanded(menu.id)}
								sx={mobileRowSx(1.5)}
							>
								<ListItemText
									primary={menu.label}
									sx={{
										"& .MuiListItemText-primary": {
											fontWeight: isActive(menu.id) ? 600 : 500,
										},
									}}
								/>
								<ExpandMore sx={chevronSx(expanded)} />
							</ListItemButton>
							<Collapse in={expanded} unmountOnExit>
								{menu.items.map((item) =>
									item.children ? (
										<Fragment key={item.key}>
											<Typography
												component="div"
												sx={{
													pl: 4,
													pt: 1,
													pb: 0.5,
													fontSize: 11,
													fontWeight: 600,
													letterSpacing: "0.08em",
													textTransform: "uppercase" as const,
													color: "text.secondary",
												}}
											>
												{item.label}
											</Typography>
											{item.children.map((leaf) => renderMobileLink(leaf, 4))}
										</Fragment>
									) : (
										renderMobileLink(item, 3)
									),
								)}
							</Collapse>
						</Fragment>
					);
				})}
			</List>
		);
	}

	return (
		<>
			{visibleMenus.map((menu) => {
				const active = isActive(menu.id);

				if (menu.items.length === 0) {
					return (
						<Button
							key={menu.id}
							id={`${menu.id}-trigger`}
							component={Link}
							to={menu.to ?? "#"}
							onClick={navigate}
							sx={triggerSx(active, false)}
						>
							{menu.label}
						</Button>
					);
				}

				const open = openMenuId === menu.id;
				const buttonId = `${menu.id}-trigger`;
				const submenuItem = open
					? menu.items.find((item) => item.key === submenu?.key)
					: undefined;

				return (
					<Fragment key={menu.id}>
						<Button
							ref={(node: HTMLElement | null) => {
								triggerRefs.current[menu.id] = node;
							}}
							id={buttonId}
							aria-controls={open ? `${menu.id}-menu` : undefined}
							aria-haspopup="menu"
							aria-expanded={open ? "true" : undefined}
							onClick={() => (open ? closeMenu() : openMenu(menu.id))}
							onMouseEnter={() => handleTriggerHover(menu.id)}
							onMouseLeave={clearHoverTimeout}
							onKeyDown={(event) => handleTriggerKeyDown(event, menu.id)}
							endIcon={<ExpandMore sx={chevronSx(open)} />}
							sx={triggerSx(active, open)}
						>
							{menu.label}
						</Button>
						<Popper
							open={open}
							anchorEl={triggerRefs.current[menu.id]}
							placement="bottom-start"
							modifiers={MENU_POPPER_MODIFIERS}
							sx={(theme) => ({ zIndex: theme.zIndex.modal })}
						>
							<ClickAwayListener
								onClickAway={(event) => {
									const trigger = triggerRefs.current[menu.id];
									if (trigger?.contains(event.target as Node)) return;
									closeMenu();
								}}
							>
								<Paper
									sx={(theme: Theme) => ({
										minWidth: 200,
										py: 0.5,
										border: `1px solid ${theme.palette.divider}`,
										borderRadius: 1.5,
										boxShadow: theme.shadows[4],
									})}
									onKeyDown={(event) => handleMenuKeyDown(event, menu.id)}
								>
									<MenuList
										id={`${menu.id}-menu`}
										aria-labelledby={buttonId}
										autoFocusItem={open && focusOnOpen}
										disablePadding
									>
										{menu.items.map((item) =>
											item.children ? (
												<MenuItem
													key={item.key}
													selected={isActive(item.key)}
													aria-haspopup="menu"
													aria-expanded={
														submenu?.key === item.key ? "true" : undefined
													}
													onMouseEnter={(event: MouseEvent<HTMLElement>) =>
														openSubmenu(item.key, event.currentTarget, false)
													}
													onClick={(event) =>
														openSubmenu(item.key, event.currentTarget, true)
													}
													onKeyDown={(event) => {
														if (event.key === "ArrowRight") {
															event.preventDefault();
															openSubmenu(item.key, event.currentTarget, true);
														}
													}}
													sx={[
														menuItemSx,
														submenu?.key === item.key && {
															backgroundColor: "action.hover",
														},
													]}
												>
													<Box sx={{ flex: 1 }}>{item.label}</Box>
													<ChevronRight
														fontSize="small"
														sx={{ mr: -0.5, opacity: 0.6 }}
													/>
												</MenuItem>
											) : (
												<MenuItem
													key={item.key}
													component={Link}
													to={item.to ?? "#"}
													selected={isActive(item.key)}
													onClick={navigate}
													onMouseEnter={closeSubmenu}
													sx={menuItemSx}
												>
													{item.label}
												</MenuItem>
											),
										)}
									</MenuList>
									<Popper
										open={submenuItem?.children !== undefined}
										anchorEl={submenu?.anchor}
										placement="right-start"
										disablePortal
										modifiers={SUBMENU_POPPER_MODIFIERS}
										sx={{ zIndex: 1 }}
									>
										<Paper
											sx={(theme: Theme) => ({
												minWidth: 200,
												py: 0.5,
												border: `1px solid ${theme.palette.divider}`,
												borderRadius: 1.5,
												boxShadow: theme.shadows[4],
											})}
											onKeyDown={handleSubmenuKeyDown}
										>
											<MenuList
												aria-label={submenuItem?.label}
												autoFocusItem={submenu?.focus ?? false}
												disablePadding
											>
												{submenuItem?.children?.map((leaf) => (
													<MenuItem
														key={leaf.key}
														component={Link}
														to={leaf.to ?? "#"}
														selected={isActive(leaf.key)}
														onClick={navigate}
														sx={menuItemSx}
													>
														{leaf.label}
													</MenuItem>
												))}
											</MenuList>
										</Paper>
									</Popper>
								</Paper>
							</ClickAwayListener>
						</Popper>
					</Fragment>
				);
			})}
		</>
	);
};
